import * as React from 'react';
import { createSlice, configureStore, bindActionCreators } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, persistStore } from 'redux-persist';
import '@stripe/stripe-js';
import { createSelectorHook, createDispatchHook, Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
const isClient = typeof window === 'object';
const formatCurrencyString = _ref => {
  let {
    value,
    currency,
    language = isClient ? navigator.language : 'en-US'
  } = _ref;
  const numberFormat = new Intl.NumberFormat(language, {
    style: 'currency',
    currency,
    currencyDisplay: 'symbol'
  });
  const parts = numberFormat.formatToParts(value);
  let zeroDecimalCurrency = true;
  for (const part of parts) {
    if (part.type === 'decimal') {
      zeroDecimalCurrency = false;
      break;
    }
  }
  value = zeroDecimalCurrency ? value : parseFloat((value / 100).toFixed(2));
  return numberFormat.format(value);
};
function updateFormattedTotalPrice(state) {
  state.formattedTotalPrice = formatCurrencyString({
    value: state.totalPrice,
    currency: state.currency,
    language: state.language
  });
}
function updateFormattedValue(state, id) {
  state.cartDetails[id].formattedValue = formatCurrencyString({
    value: state.cartDetails[id].value,
    currency: state.currency,
    language: state.language
  });
}
function updateFormattedPrice(state, id) {
  state.cartDetails[id].formattedPrice = formatCurrencyString({
    value: state.cartDetails[id].price,
    currency: state.currency,
    language: state.language
  });
}
function Entry(_ref2) {
  let {
    id,
    product,
    quantity,
    price_metadata,
    product_metadata
  } = _ref2;
  return {
    ...product,
    id,
    quantity,
    value: product.price * quantity,
    price_data: {
      ...product.price_data,
      ...price_metadata
    },
    product_data: {
      ...product.product_data,
      ...product_metadata
    }
  };
}
function createEntry(_ref3) {
  let {
    state,
    id,
    product,
    count,
    price_metadata,
    product_metadata
  } = _ref3;
  const entry = Entry({
    id,
    product,
    quantity: count,
    price_metadata,
    product_metadata
  });
  state.cartDetails[id] = entry;
  updateFormattedValue(state, id);
  updateFormattedPrice(state, id);
  state.totalPrice += entry.value;
  state.cartCount += count;
  updateFormattedTotalPrice(state);
}
function updateEntry(_ref4) {
  let {
    state,
    id,
    count,
    price_metadata,
    product_metadata
  } = _ref4;
  const entry = state.cartDetails[id];
  if (entry.quantity + count <= 0) return removeEntry({
    state,
    id
  });
  state.cartDetails[id] = Entry({
    id,
    state,
    product: entry,
    quantity: entry.quantity + count,
    price_metadata,
    product_metadata
  });
  updateFormattedValue(state, id);
  state.totalPrice += entry.price * count;
  state.cartCount += count;
  updateFormattedTotalPrice(state);
}
function removeEntry(_ref5) {
  let {
    state,
    id
  } = _ref5;
  const cartDetails = state.cartDetails;
  state.totalPrice -= cartDetails[id].value;
  state.cartCount -= cartDetails[id].quantity;
  delete cartDetails[id];
  updateFormattedTotalPrice(state);
}
function updateQuantity(_ref6) {
  let {
    state,
    id,
    quantity
  } = _ref6;
  const entry = state.cartDetails[id];
  updateEntry({
    state,
    id,
    count: quantity - entry.quantity
  });
}
const initialState = {
  cartMode: 'checkout-session',
  mode: 'payment',
  currency: 'USD',
  language: isClient ? navigator.language : 'en-US',
  lastClicked: '',
  shouldDisplayCart: false,
  cartCount: 0,
  totalPrice: 0,
  formattedTotalPrice: '',
  cartDetails: {},
  stripe: '',
  shouldPersist: true
};
const slice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItem: {
      reducer(state, _ref7) {
        let {
          payload: {
            product,
            options
          }
        } = _ref7;
        const {
          count,
          price_metadata,
          product_metadata
        } = options;
        const id = product.id || product.price_id || product.sku_id || product.sku || v4();
        if (id in state.cartDetails) {
          updateEntry({
            state,
            id,
            count,
            price_metadata,
            product_metadata
          });
        } else {
          createEntry({
            state,
            id,
            product,
            count,
            price_metadata,
            product_metadata
          });
        }
      },
      prepare(product) {
        let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
          count: 1
        };
        if (!options.price_metadata) options.price_metadata = {};
        if (!options.product_metadata) options.product_metadata = {};
        if (!options.count) options.count = 1;
        return {
          payload: {
            product,
            options
          }
        };
      }
    },
    incrementItem: {
      reducer(state, _ref8) {
        let {
          payload: {
            id,
            options
          }
        } = _ref8;
        updateEntry({
          state,
          id,
          count: options.count
        });
      },
      prepare(id) {
        let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
          count: 1
        };
        return {
          payload: {
            id,
            options
          }
        };
      }
    },
    decrementItem: {
      reducer(state, _ref9) {
        let {
          payload: {
            id,
            options
          }
        } = _ref9;
        if (state.cartDetails[id].quantity - options.count <= 0) return removeEntry({
          state,
          id
        });
        updateEntry({
          state,
          id,
          count: -options.count
        });
      },
      prepare(id) {
        let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
          count: 1
        };
        return {
          payload: {
            id,
            options
          }
        };
      }
    },
    clearCart: {
      reducer(state) {
        state.cartCount = 0;
        state.totalPrice = 0;
        state.cartDetails = {};
        updateFormattedTotalPrice(state);
      }
    },
    setItemQuantity: {
      reducer(state, _ref10) {
        let {
          payload: {
            id,
            quantity
          }
        } = _ref10;
        if (quantity > 0 && id in state.cartDetails) return updateQuantity({
          ...state,
          state,
          id,
          quantity
        });else if (quantity === 0) return removeEntry({
          state,
          id
        });
      },
      prepare(id, quantity) {
        return {
          payload: {
            id,
            quantity
          }
        };
      }
    },
    removeItem: {
      reducer(state, _ref11) {
        let {
          payload: {
            id
          }
        } = _ref11;
        removeEntry({
          state,
          id
        });
      },
      prepare(id) {
        return {
          payload: {
            id
          }
        };
      }
    },
    loadCart: {
      reducer(state, _ref12) {
        let {
          payload: {
            cartDetails,
            shouldMerge
          }
        } = _ref12;
        if (!shouldMerge) {
          state.cartCount = 0;
          state.totalPrice = 0;
          state.cartDetails = {};
        }
        for (const id in cartDetails) {
          const entry = cartDetails[id];
          createEntry({
            state,
            id: entry.id,
            product: entry,
            count: entry.quantity
          });
        }
      },
      prepare(cartDetails) {
        let shouldMerge = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
        return {
          payload: {
            cartDetails,
            shouldMerge
          }
        };
      }
    },
    handleCartHover(state) {
      state.shouldDisplayCart = true;
    },
    handleCartClick(state) {
      state.shouldDisplayCart = !state.shouldDisplayCart;
    },
    handleCloseCart(state) {
      state.shouldDisplayCart = false;
    },
    storeLastClicked(state, _ref13) {
      let {
        payload
      } = _ref13;
      state.lastClicked = payload;
    },
    changeStripeKey(state, _ref14) {
      let {
        payload
      } = _ref14;
      state.stripe = payload;
    },
    changeLanguage(state, _ref15) {
      let {
        payload
      } = _ref15;
      state.language = payload;
    },
    changeCurrency(state, _ref16) {
      let {
        payload
      } = _ref16;
      state.currency = payload;
    }
  }
});
slice.actions.redirectToCheckout = sessionId => ({
  type: 'cart/redirectToCheckout',
  payload: {
    sessionId
  }
});
slice.actions.checkoutSingleItem = itemsOrPriceId => {
  const quantity = itemsOrPriceId.quantity || 1;
  const cartItems = (() => {
    if (typeof itemsOrPriceId === 'string') {
      return {
        lineItems: [{
          price: itemsOrPriceId,
          quantity
        }]
      };
    }
    if (Object.prototype.hasOwnProperty.call(itemsOrPriceId, 'price')) {
      return {
        lineItems: [{
          price: itemsOrPriceId.price,
          quantity
        }]
      };
    }
    /**
     * Backward compatibility (SKU)
     */
    if (Object.prototype.hasOwnProperty.call(itemsOrPriceId, 'sku')) {
      return {
        items: [{
          sku: itemsOrPriceId.sku,
          quantity
        }]
      };
    }
    return [];
  })();
  return {
    type: 'cart/checkoutSingleItem',
    payload: {
      cartItems
    }
  };
};
const {
  reducer,
  actions
} = slice;

/**
 * Returns the constructor name of any value.
 * @param {any} value
 * @returns string
 */
function typeOf(value) {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  return value.constructor.name;
}
class PropertyValueError extends Error {
  constructor(_ref17) {
    let {
      property,
      method,
      expected,
      received
    } = _ref17;
    const plural = Array.isArray(expected);
    const expectedString = plural ? "are ".concat(expected.map(v => JSON.stringify(v)).join(', ')) : "is ".concat(JSON.stringify(expected));
    const methodString = typeof method === 'string' ? " in ".concat(method, "()") : '';
    super("Invalid value ".concat(JSON.stringify(received), " was received for ").concat(property, ". Valid ").concat(plural ? 'values' : 'value', " for ").concat(property).concat(methodString, " ").concat(expectedString, "."));
    this.name = 'PropertyValueError';
  }
}
class PropertyTypeError extends TypeError {
  constructor(_ref18) {
    let {
      property,
      expected,
      received
    } = _ref18;
    const plural = Array.isArray(expected);
    const expectedString = plural ? "are ".concat(expected.map(v => JSON.stringify(v)).join(', ')) : "is ".concat(JSON.stringify(expected));
    super("Invalid value with type ".concat(JSON.stringify(received), " was received for ").concat(property, ". Valid ").concat(plural ? 'types' : 'type', " for ").concat(property, " ").concat(expectedString, "."));
    this.name = 'PropertyTypeError';
  }
}
class PropertyRangeError extends RangeError {
  constructor(_ref19) {
    let {
      property,
      between,
      below,
      above,
      received
    } = _ref19;
    let expectedString;
    if (Array.isArray(between)) expectedString = "between ".concat(between[0], " and ").concat(between[1]);else if (['string', 'number'].includes(typeof below)) expectedString = "below ".concat(below);else if (['string', 'number'].includes(typeof above)) expectedString = "above ".concat(above);
    super("Invalid value ".concat(JSON.stringify(received), " was received for ").concat(property, ". Valid range for ").concat(property, " is ").concat(expectedString, "."));
    this.name = 'PropertyRangeError';
  }
}
function _optionalChain$1(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn(function () {
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        return value.call(lastAccessLHS, ...args);
      });
      lastAccessLHS = undefined;
    }
  }
  return value;
}
function getCheckoutData(cart) {
  const lineItems = [];
  for (const sku in cart.cartDetails) lineItems.push({
    price: sku,
    quantity: cart.cartDetails[sku].quantity
  });
  const options = {
    mode: cart.mode,
    lineItems,
    successUrl: cart.successUrl,
    cancelUrl: cart.cancelUrl,
    billingAddressCollection: cart.billingAddressCollection ? 'required' : 'auto',
    submitType: 'auto'
  };
  if (_optionalChain$1([cart, 'access', _ => _.allowedCountries, 'optionalAccess', _2 => _2.length])) {
    options.shippingAddressCollection = {
      allowedCountries: cart.allowedCountries
    };
  }
  return options;
}
const handleStripe = store => next => async action => {
  const stripePublicKey = store.getState().stripe;
  const cart = store.getState();
  const checkout = ['cart/redirectToCheckout', 'cart/checkoutSingleItem'];
  if (checkout.includes(action.type)) {
    if (typeof stripePublicKey !== 'string') {
      throw new PropertyTypeError({
        property: 'stripe',
        expected: 'string',
        received: typeOf(stripePublicKey)
      });
    } else if (stripePublicKey.length === 0) {
      throw new PropertyRangeError({
        property: 'stripe.length',
        above: 0,
        received: stripePublicKey.length
      });
    }
  }
  if (action.type === 'cart/redirectToCheckout') {
    const stripe = initializeStripe(stripePublicKey);
    if (cart.cartMode === 'checkout-session') {
      return stripe.redirectToCheckout({
        sessionId: action.payload.sessionId
      });
    } else if (cart.cartMode === 'client-only') {
      const checkoutData = getCheckoutData(cart);
      return stripe.redirectToCheckout(checkoutData);
    } else {
      throw new PropertyValueError({
        property: 'cartMode',
        method: 'redirectToCheckout',
        expected: ['client-only', 'checkout-session'],
        received: cart.cartMode
      });
    }
  } else if (action.type === 'cart/checkoutSingleItem') {
    const stripe = initializeStripe(stripePublicKey);
    if (cart.cartMode === 'client-only') {
      const options = {
        mode: cart.mode,
        successUrl: cart.successUrl,
        cancelUrl: cart.cancelUrl,
        ...action.payload.cartItems
      };
      return stripe.redirectToCheckout(options);
    } else {
      throw new PropertyValueError({
        property: 'cartMode',
        method: 'checkoutSingleItem',
        expected: 'client-only',
        received: cart.cartMode
      });
    }
  }
  return next(action);
};
function initializeStripe(publicKey) {
  try {
    // eslint-disable-next-line no-undef
    const stripe = Stripe(publicKey);
    stripe.registerAppInfo({
      name: 'use-shopping-cart',
      version: "3.1.3",
      url: 'https://useshoppingcart.com',
      // eslint-disable-next-line camelcase
      partner_id: 'pp_partner_H8MLmI3e9Oc3IK'
    });
    return stripe;
  } catch (error) {
    console.error('Unable to initialize Stripe.');
    throw error;
  }
}
const handleWarnings = store => next => async action => {
  const count = ['cart/addItem', 'cart/incrementItem', 'cart/decrementItem'];
  if (count.includes(action.type) && typeof action.payload.options.count !== 'number') {
    console.warn("Invalid count used with action ".concat(action.type, ": count must be a number. The current type is ").concat(typeOf(action.payload.options.count), "."), action);
    return;
  }
  const quantity = ['cart/setItemQuantity'];
  if (quantity.includes(action.type)) {
    if (typeof action.payload.quantity !== 'number') {
      console.warn("Invalid quantity used with action ".concat(action.type, ": quantity must be a number. The current type is ").concat(typeOf(action.payload.quantity), "."), action);
      return;
    } else if (action.payload.quantity < 0) {
      console.warn("Invalid quantity used with action ".concat(action.type, ": quantity must be greater than zero. The current value is ").concat(JSON.stringify(action.payload.quantity), "."), action);
      return;
    }
  }
  const id = ['cart/incrementItem', 'cart/decrementItem', 'cart/removeItem'];
  if (id.includes(action.type) && !(action.payload.id in store.getState().cartDetails)) {
    console.warn("Invalid product ID used with action ".concat(action.type, ": the ID must already be in the cart. The current value is ").concat(JSON.stringify(action.payload.id), "."), action);
    return;
  }
  return next(action);
};
function _nullishCoalesce(lhs, rhsFn) {
  if (lhs != null) {
    return lhs;
  } else {
    return rhsFn();
  }
}
async function filterCart(cartDetails, filter) {
  const filteredCart = {};
  for (const sku in cartDetails) {
    const entry = cartDetails[sku];
    if (await filter(entry)) filteredCart[sku] = entry;
  }
  return filteredCart;
}
function noop() {}
function createNoopStorage() {
  return {
    getItem: noop,
    setItem: noop,
    removeItem: noop
  };
}
function createLocalStorage() {
  return {
    async getItem(key) {
      return window.localStorage.getItem(key);
    },
    async setItem(key, value) {
      return window.localStorage.setItem(key, value);
    },
    async removeItem(key) {
      return window.localStorage.removeItem(key);
    }
  };
}
function createShoppingCartStore(options) {
  options.shouldPersist = _nullishCoalesce(options.shouldPersist, () => true);
  if (!isClient) {
    return configureStore({
      reducer,
      preloadedState: {
        ...initialState,
        ...options
      }
    });
  }
  let storage;
  if (isClient) storage = options.storage || createLocalStorage();else storage = createNoopStorage();
  delete options.storage;
  const persistConfig = {
    key: 'root',
    version: 1,
    storage,
    whitelist: ['cartCount', 'totalPrice', 'formattedTotalPrice', 'cartDetails']
  };
  const persistedReducer = persistReducer(persistConfig, reducer);
  const newInitialState = {
    ...initialState,
    ...options
  };
  updateFormattedTotalPrice(newInitialState);
  return configureStore({
    reducer: options.shouldPersist ? persistedReducer : reducer,
    preloadedState: newInitialState,
    middleware: getDefaultMiddleware => getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: options.shouldPersist ? [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER] : ['persist/PERSIST']
      }
    }).concat(handleStripe, handleWarnings)
  });
}
const _jsxFileName = "/Users/nickdejesus/Code/hooks/use-shopping-cart/use-shopping-cart/react/index.js";
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn(function () {
        for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
          args[_key2] = arguments[_key2];
        }
        return value.call(lastAccessLHS, ...args);
      });
      lastAccessLHS = undefined;
    }
  }
  return value;
}
const CartContext = React.createContext(initialState);
const useSelector = createSelectorHook(CartContext);
const useDispatch = createDispatchHook(CartContext);
function CartProvider(_ref20) {
  let {
    loading = null,
    children,
    ...props
  } = _ref20;
  const store = React.useMemo(() => createShoppingCartStore(props), [props]);
  const persistor = persistStore(store);
  if (_optionalChain([props, 'optionalAccess', _ => _.shouldPersist]) || props.shouldPersist === undefined) {
    return React.createElement(Provider, {
      context: CartContext,
      store: store,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 24
      }
    }, React.createElement(PersistGate, {
      persistor: persistor,
      children: bootstrapped => {
        if (!bootstrapped) return loading;
        return children;
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 25
      }
    }));
  } else {
    return React.createElement(Provider, {
      context: CartContext,
      store: store,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 36
      }
    }, children);
  }
}
function useShoppingCart() {
  let selector = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : state => ({
    ...state
  });
  let equalityFn = arguments.length > 1 ? arguments[1] : undefined;
  const dispatch = useDispatch();
  const cartState = useSelector(selector, equalityFn);
  const shoppingCart = React.useMemo(() => {
    const cartActions = bindActionCreators(actions, dispatch);
    return {
      ...cartState,
      ...cartActions
    };
  }, [cartState, dispatch]);
  React.useDebugValue(shoppingCart);
  return shoppingCart;
}
function DebugCart(props) {
  const cart = useShoppingCart(state => state);
  const cartPropertyRows = Object.entries(cart).filter(_ref21 => {
    let [, value] = _ref21;
    return typeof value !== 'function';
  }).map(_ref22 => {
    let [key, value] = _ref22;
    return React.createElement('tr', {
      key: key,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 64
      }
    }, React.createElement('td', {
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 65
      }
    }, key), React.createElement('td', {
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 66
      }
    }, typeof value === 'object' ? React.createElement('button', {
      onClick: () => console.log(value),
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 68
      }
    }, "Log value") : JSON.stringify(value)));
  });
  return React.createElement('div', {
    style: {
      position: 'fixed',
      top: 50,
      right: 50,
      backgroundColor: '#eee',
      textAlign: 'left',
      maxWidth: 300,
      padding: 20,
      borderSpacing: '25px 5px',
      overflow: 'auto'
    },
    ...props,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 77
    }
  }, React.createElement('table', {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 91
    }
  }, React.createElement('thead', {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 92
    }
  }, React.createElement('tr', {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 93
    }
  }, React.createElement('th', {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 94
    }
  }, "Key"), React.createElement('th', {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 95
    }
  }, "Value"))), React.createElement('tbody', {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 98
    }
  }, cartPropertyRows)));
}
export { CartContext, CartProvider, DebugCart, actions, filterCart, formatCurrencyString, useDispatch, useSelector, useShoppingCart };